import { useMemo } from 'react';
import { Field, FieldProps, Formik } from 'formik';

import {
  SButton,
  SFormikRadio,
  SFormikSelect,
  SFormikTextfield
} from '@setvi/shared/components';
import { useCustomErrors } from '@setvi/shared/hooks';
import SLabel from '@setvi/shared/components/sui/slabel';
import {
  mehanicalRequirements,
  locations,
  pizzaHours,
  pizzaSizes
} from '../../dummy';
import {
  VALIDATORS,
  pizzaSchema,
  sizeValidation,
  hoursValidation,
  locationValidation,
  commentsValidation
} from './utils';
import { formId } from '../../../product-match/components/form/text';
import { Footer, Container, StyledSlider, inputStyle } from './styled';
import { ProductMatchSubmitButton } from '../../../product-match/components/form/components';

interface Props {
  values: { [key: string]: string };
  disableButton?: boolean;
  onSubmit?: (c: { [key: string]: string }) => void;
  onChange?: (value: { [key: string]: string }) => void;
}

const PizzaForm = ({ values, disableButton, onSubmit, onChange }: Props) => {
  const { customErrors, customValidate } = useCustomErrors({
    schemas: pizzaSchema,
    values
  });

  const initialValues = useMemo(() => values, [values]);

  const isDisabled = (
    v: { [key: string]: string | number },
    db: boolean,
    c?: { [key: string]: any }
  ): boolean =>
    db ||
    !v?.location ||
    !v?.size ||
    !v?.hours ||
    !v?.requirements ||
    c?.location ||
    c?.size ||
    c?.hours ||
    c?.comments;

  return (
    <Formik
      onSubmit={c => {
        onSubmit?.(c);
        onChange?.(c);
      }}
      enableReinitialize
      validateOnChange={false}
      initialValues={initialValues}>
      {({ values: formikValues, resetForm }) => (
        <Container id={formId}>
          <SFormikSelect
            name="location"
            required
            label="Where will the Pizza Oven be used?"
            placeholder="Select location"
            options={
              locations?.map(item => ({
                ...item,
                value: item?.label
              })) || []
            }
            style={inputStyle}
            onChange={(value: string) =>
              customValidate({
                schema: locationValidation,
                value,
                name: 'location'
              })
            }
            customErrors={customErrors}
          />

          <Field name="size">
            {({ meta, form }: FieldProps) => (
              <>
                <SLabel
                  title="Max Pizza size in inches, you would need to cook?"
                  variant="body2"
                  required
                />
                <StyledSlider
                  step={1}
                  min={9}
                  max={20}
                  defaultValue={9}
                  marks={[
                    pizzaSizes[0],
                    {
                      value: meta.value,
                      label: `${meta.value}”`
                    },
                    pizzaSizes[1]
                  ]}
                  value={meta.value}
                  valueLabelDisplay="auto"
                  aria-labelledby="size-slider"
                  getAriaValueText={value => `${value}”`}
                  onChange={(_, value) => {
                    form.setFieldValue('size', value);

                    customValidate({
                      schema: sizeValidation,
                      value,
                      name: 'size'
                    });
                  }}
                />
              </>
            )}
          </Field>

          <Field name="hours">
            {({ meta, form }: FieldProps) => (
              <>
                <SLabel
                  title="Number of Pizza's you would need to cook in an hour?"
                  variant="body2"
                  required
                />
                <StyledSlider
                  step={5}
                  min={0}
                  max={100}
                  defaultValue={0}
                  marks={[
                    pizzaHours[0],
                    {
                      value: meta.value,
                      label: `${meta.value}`
                    },
                    pizzaHours[1]
                  ]}
                  value={meta.value}
                  valueLabelDisplay="auto"
                  aria-labelledby="hours-slider"
                  getAriaValueText={value => `${value}”`}
                  onChange={(_, value) => {
                    form.setFieldValue('hours', value);

                    customValidate({
                      schema: hoursValidation,
                      value,
                      name: 'hours'
                    });
                  }}
                />
              </>
            )}
          </Field>

          <SFormikRadio
            row
            required
            name="requirements"
            label="Gas or Electric?"
            options={mehanicalRequirements}
          />

          <SFormikTextfield
            name="comments"
            placeholder="Type your comments..."
            label="Additional Comments"
            maxLength={VALIDATORS.COMMENTS}
            onChange={(value: string) => {
              customValidate({
                schema: commentsValidation,
                value,
                name: 'comments'
              });
            }}
            InputProps={{
              style: inputStyle
            }}
            multiline
            customErrors={customErrors}
          />

          <Footer>
            <SButton
              variant="outlined"
              color="default"
              style={{
                borderRadius: 120
              }}
              onClick={() => {
                resetForm();
                onChange?.({
                  type: '',
                  location: '',
                  size: '',
                  hours: '',
                  requirements: '',
                  comments: ''
                });
              }}>
              Back
            </SButton>

            <ProductMatchSubmitButton
              expanded
              disabled={isDisabled(formikValues, disableButton, customErrors)}
            />
          </Footer>
        </Container>
      )}
    </Formik>
  );
};

export default PizzaForm;
